/**
 * The analytics context an embedder states in its own markup.
 *
 * Inline on a venue's own page there is no checkout URL to carry our parameters:
 * the URL belongs to the venue. The embed snippet is the only place the venue can
 * state who owns the journey and where their own measurement goes, so the same
 * context the URL carries is also accepted as attributes on the custom element:
 *
 * ```html
 * <guest-checkout source="partner" google-analytics-code="G-XXXXXXX"></guest-checkout>
 * ```
 *
 * Attribute names follow the legacy booking widget's where one exists
 * (`google-analytics-code`, `google-tag-manager-code`), so an existing venue
 * snippet reads naturally, with the URL parameter names accepted as aliases. See
 * `docs/analytics.md`.
 */
import type { PartnerAnalyticsContext } from "./types";
import { resolveWidgetElement } from "./widgetElement";

/**
 * Attributes read for each piece of context, in the order they are consulted:
 * the legacy widget's own name first, then the URL parameter name, so either
 * spelling works in markup.
 */
export const ANALYTICS_ELEMENT_ATTRIBUTES = {
  source: ["source"],
  affiliateId: ["affiliate-id", "affiliate_id"],
  partnerGaAccount: ["google-analytics-code", "ga-account", "ga_account"],
  partnerGtmAccount: ["google-tag-manager-code", "gtm-account", "gtm_account"],
} as const satisfies Record<keyof PartnerAnalyticsContext, readonly string[]>;

/** A stated value, or `null` when the attribute is absent or blank. */
function readAttribute(element: Element, names: readonly string[]): string | null {
  for (const name of names) {
    const value = element.getAttribute(name)?.trim();
    if (value) return value;
  }

  return null;
}

/**
 * Reads the analytics context the embedder stated on the custom element.
 *
 * Every value is `null` in the standalone build and wherever the embedder stated
 * nothing, which is what leaves a DesignMyNight-hosted journey behaving exactly
 * as it did before.
 *
 * @param element element to read; defaults to the custom element the checkout is
 * mounted in.
 */
export function readElementAnalyticsContext(element: Element | null = resolveWidgetElement()): PartnerAnalyticsContext {
  if (!element) return { source: null, affiliateId: null, partnerGaAccount: null, partnerGtmAccount: null };

  return {
    source: readAttribute(element, ANALYTICS_ELEMENT_ATTRIBUTES.source),
    affiliateId: readAttribute(element, ANALYTICS_ELEMENT_ATTRIBUTES.affiliateId),
    partnerGaAccount: readAttribute(element, ANALYTICS_ELEMENT_ATTRIBUTES.partnerGaAccount),
    partnerGtmAccount: readAttribute(element, ANALYTICS_ELEMENT_ATTRIBUTES.partnerGtmAccount),
  };
}
